import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import apiRequest from "../services/api";
import { FaSpinner, FaCloudUploadAlt, FaCheckCircle } from "react-icons/fa";

const Callback = ({ onLogin }) => {
  const [status, setStatus] = useState("Authenticating with Fayda...");
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [faydaData, setFaydaData] = useState(null);

  const [linkStep, setLinkStep] = useState(1);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");

  const [files, setFiles] = useState({
    cert: null,
  });

  const hasCalled = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const cacheKey = localStorage.getItem("cacheKey");
  const storedFan = localStorage.getItem("fan");

  const [forgotEmail, setForgotEmail] = useState(storedFan || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const [resetForm, setResetForm] = useState({
    username: storedFan || "",
    token: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    const processLogin = async () => {
      if (hasCalled.current) return;
      hasCalled.current = true;
      try {
        const params = new URLSearchParams(location.search);
        const code = params.get("code");
        const state = params.get("state");
        const savedState = sessionStorage.getItem("fayda_state");
        const codeVerifier = sessionStorage.getItem("fayda_verifier");

        if (state !== savedState) throw new Error("Invalid session state.");

        const response = await apiRequest("auth/fayda/callback", {
          method: "POST",
          body: { code, codeVerifier, cacheKey },
        });

        if (response.success) {
          handleSuccessfulAuth(response);
        } else if (response.success === false) {
          setFaydaData(response);
          setShowModal(true);
          setStatus("Identity Verified");
        }
      } catch (err) {
        setError(err.message || "Failed to complete Fayda authentication.");
      }
    };
    processLogin();
  }, [location, navigate, onLogin]);

  const handleSuccessfulAuth = (userData) => {
    localStorage.setItem("token", userData.token);
    localStorage.setItem("user", JSON.stringify(userData));
    if (onLogin) onLogin(userData);
    sessionStorage.clear();
    navigate("/");
  };

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    setFiles((prev) => ({ ...prev, [name]: selectedFiles[0] }));
  };

  // HELPER: Convert Base64 to Blob for file upload
  const base64ToBlob = (base64, mime) => {
    const byteString = atob(base64.split(",")[1] || base64);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mime || "image/jpeg" });
  };

  const finalizeLink = async (e) => {
    e.preventDefault();

    const profileImg = faydaData?.photoUrl || faydaData?.picture;
    const activeFan = storedFan || faydaData?.username || faydaData?.sub;

    if (!profileImg) {
      return setModalError("Fayda profile picture is missing.");
    }

    if (!files.cert) {
      return setModalError("Please upload your Educational Certificate.");
    }

    setModalLoading(true);
    setModalError("");
    setSuccessMsg("");

    try {
      const formData = new FormData();
      const photoBlob = base64ToBlob(profileImg, "image/jpeg");
      formData.append("photo", photoBlob, "fayda_profile.jpg");
      formData.append("cert", files.cert);

      const response = await apiRequest(
        `auth/fayda/finalizelink/${activeFan}`,
        {
          method: "POST",
          body: formData,
        }
      );

      const key = Number(response.akey ?? 0);

      // Force password reset if akey != 0
      if (key !== 0) {
        sessionStorage.clear();
        setForgotEmail(activeFan);
        setResetForm((prev) => ({
          ...prev,
          username: activeFan,
        }));

        setModalError("");
        setSuccessMsg("");
        setLinkStep(2); // Move to Password Recovery
        return;
      }

      // Standard Login path
      if (response.token) {
        handleSuccessfulAuth(response);
      }
    } catch (err) {
      setModalError(err.message || "Failed to link account.");
    } finally {
      setModalLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setModalError("");
    setSuccessMsg("");

    if (!forgotEmail) {
      setModalError("Please enter your Fayda FAN.");
      return;
    }

    try {
      setIsSubmitting(true);

      await apiRequest(
        `auth/forgotPassword?username=${encodeURIComponent(forgotEmail)}`,
        { method: "PUT" }
      );

      setResetForm((prev) => ({
        ...prev,
        username: forgotEmail,
      }));

      setSuccessMsg("OTP sent to your registered mobile.");
      setModalError("");
      setLinkStep(3); // Move to OTP Reset Password
    } catch (err) {
      setModalError(err.message || "Unable to send OTP.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setModalError("");
    setSuccessMsg("");

    if (!resetForm.token) {
      setModalError("Please enter the OTP.");
      return;
    }

    if (!resetForm.newPassword) {
      setModalError("Please enter a new password.");
      return;
    }

    if (resetForm.newPassword !== resetForm.confirmPassword) {
      setModalError("Passwords do not match.");
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await apiRequest(
        `auth/resetPassword?username=${encodeURIComponent(
          resetForm.username
        )}&token=${encodeURIComponent(
          resetForm.token
        )}&newPassword=${encodeURIComponent(resetForm.newPassword)}`,
        { method: "PUT" }
      );

      setSuccessMsg("Password changed successfully.");

      // If response includes token, log user in directly
      if (response && response.token) {
        handleSuccessfulAuth(response);
      } else {
        // Fallback if backend doesn't return JWT token upon password reset
        setShowModal(false);
        navigate("/");
      }
    } catch (err) {
      setModalError(err.message || "Unable to reset password.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getProfileImage = () => {
    if (!faydaData) return "";
    const imgStr = faydaData.photoUrl || faydaData.picture;
    if (!imgStr) return `https://ui-avatars.com/api/?name=${faydaData.name}`;
    if (imgStr.length > 100 && !imgStr.startsWith("data:")) {
      return `data:image/jpeg;base64,${imgStr}`;
    }
    return imgStr;
  };

  if (error) return <ErrorView error={error} navigate={navigate} />;

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-slate-50">
      {!showModal && (
        <div className="flex flex-col items-center">
          <FaSpinner className="text-sky-500 text-5xl animate-spin mb-4" />
          <h2 className="text-xl font-bold text-slate-700">{status}</h2>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white max-w-md w-full rounded-3xl shadow-2xl overflow-hidden">
            <div className="bg-sky-600 p-6 text-center text-white">
              <img
                src={getProfileImage()}
                alt="Profile"
                className="w-24 h-24 rounded-full border-4 border-white mx-auto object-cover mb-3 shadow-md bg-white"
              />
              <h3 className="font-bold text-lg">Welcome, {faydaData?.name}</h3>
            </div>

            <div className="p-6">
              {modalError && (
                <p className="text-xs p-2 rounded mb-4 text-center bg-red-50 text-red-500">
                  {modalError}
                </p>
              )}

              {successMsg && (
                <p className="text-xs p-2 rounded mb-4 text-center bg-green-50 text-green-700">
                  {successMsg}
                </p>
              )}

              {linkStep === 1 && (
                <form onSubmit={finalizeLink} className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl mb-2">
                    <img
                      src={getProfileImage()}
                      className="w-10 h-10 rounded-full object-cover"
                      alt="Preview"
                    />
                    <span className="text-xs text-green-700 font-medium">
                      Profile photo automatically attached from Fayda.
                    </span>
                  </div>

                  <FileField
                    required
                    label="Educational certificate (Image or PDF format)"
                    name="cert"
                    onChange={handleFileChange}
                    file={files.cert}
                  />

                  <button
                    type="submit"
                    disabled={modalLoading}
                    className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-xl font-bold mt-4 shadow-lg transition"
                  >
                    {modalLoading ? "Uploading..." : "Complete Linking"}
                  </button>
                </form>
              )}

              {linkStep === 2 && (
                <div className="animate-in slide-in-from-right duration-300">
                  <h2 className="text-2xl font-bold text-slate-800 mb-2">
                    Password Recovery
                  </h2>
                  <p className="text-sm text-slate-500 mb-5">
                    Send an OTP to your registered mobile number.
                  </p>

                  <form onSubmit={handleForgotSubmit} className="space-y-4">
                    <input
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="w-full bg-slate-50 border-2 border-slate-100 px-4 py-3 rounded-xl focus:border-sky-400 outline-none"
                      placeholder="Username / Fayda FAN"
                    />

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-sky-500 hover:bg-sky-600 text-white font-bold py-3 rounded-xl transition"
                    >
                      {isSubmitting ? "Sending OTP..." : "Send OTP"}
                    </button>
                  </form>
                </div>
              )}

              {linkStep === 3 && (
                <div className="animate-in slide-in-from-right duration-300">
                  <h2 className="text-2xl font-bold text-slate-800 mb-2">
                    Reset Password
                  </h2>
                  <p className="text-sm text-slate-500 mb-5">
                    Enter the OTP sent to your registered mobile.
                  </p>

                  <form onSubmit={handleResetSubmit} className="space-y-4">
                    <input
                      placeholder="OTP"
                      value={resetForm.token}
                      onChange={(e) =>
                        setResetForm({
                          ...resetForm,
                          token: e.target.value,
                        })
                      }
                      className="w-full bg-slate-50 border-2 border-slate-100 px-4 py-3 rounded-xl focus:border-sky-400 outline-none"
                    />

                    <input
                      type="password"
                      placeholder="New Password"
                      value={resetForm.newPassword}
                      onChange={(e) =>
                        setResetForm({
                          ...resetForm,
                          newPassword: e.target.value,
                        })
                      }
                      className="w-full bg-slate-50 border-2 border-slate-100 px-4 py-3 rounded-xl focus:border-sky-400 outline-none"
                    />

                    <input
                      type="password"
                      placeholder="Confirm Password"
                      value={resetForm.confirmPassword}
                      onChange={(e) =>
                        setResetForm({
                          ...resetForm,
                          confirmPassword: e.target.value,
                        })
                      }
                      className="w-full bg-slate-50 border-2 border-slate-100 px-4 py-3 rounded-xl focus:border-sky-400 outline-none"
                    />

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-xl transition"
                    >
                      {isSubmitting ? "Updating..." : "Reset Password"}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FileField = ({ label, name, onChange, file }) => (
  <div className="relative border-2 border-dashed border-slate-200 rounded-xl p-3 bg-slate-50">
    <input
      type="file"
      name={name}
      onChange={onChange}
      className="absolute inset-0 opacity-0 cursor-pointer"
    />
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {file ? (
          <FaCheckCircle className="text-green-500" />
        ) : (
          <FaCloudUploadAlt className="text-slate-400" />
        )}
        <span className="text-xs font-semibold">
          {file ? file.name : label}
        </span>
      </div>
      {!file && (
        <span className="text-[10px] bg-sky-100 text-sky-600 px-2 py-0.5 rounded font-bold">
          Choose
        </span>
      )}
    </div>
  </div>
);

const ErrorView = ({ error, navigate }) => (
  <div className="flex flex-col items-center justify-center h-screen bg-slate-50 p-4">
    <div className="bg-white p-8 rounded-3xl shadow-xl text-center max-w-md w-full">
      <h2 className="text-2xl font-bold text-red-600 mb-4">{error}</h2>

      <button
        onClick={() => navigate("/")}
        className="w-full bg-sky-500 text-white py-3 rounded-xl"
      >
        Back
      </button>
    </div>
  </div>
);

export default Callback;
